import clsx from 'clsx';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import heaxagon from '../../../assets/image/hexagonMultiple.png';
import { Button } from '../Button';

gsap.registerPlugin(ScrollTrigger);

export const CTA = ({
  classNameHeight,
  dark = false,
  fullHeight = true,
  primaryButtonText,
  showEstimationButton = true,
  transparent = false,
  ...props
}: {
  classNameHeight?: string;
  dark?: boolean;
  fullHeight?: boolean;
  href?: string;
  primaryButtonText?: string;
  showEstimationButton?: boolean;
  subtitle?: string;
  title?: string;
  transparent?: boolean;
  text?: string;
}) => {
  const { t } = useTranslation('common');

  const primaryHref = props.href ? props.href : t('common:contactProjectSlug');
  const subtitle = props.hasOwnProperty('subtitle') ? props.subtitle : t('ctaSection.gotAProject');
  const title = props.title ? props.title : t('ctaSection.letsDiscussYourIdea');
  const text = props.hasOwnProperty('text') ? props.text : t('ctaSection.text');
  const primaryCtaText = primaryButtonText || t('ctaSection.ctaButton');
  return (
    <section
      id="cta"
      className={clsx(
        'relative flex w-full items-center justify-center overflow-hidden px-8 py-40 text-center text-white',
        {
          'bg-gradient': dark && !transparent,
          'bg-white': !dark && !transparent,
          'lg:min-h-[70vh]': !fullHeight && !classNameHeight,
          [classNameHeight as string]: classNameHeight,
        },
      )}>
      {!transparent && (
        <Image
          className="absolute bottom-0 right-[-3vw] h-full w-auto -skew-y-6 object-contain opacity-5"
          src={heaxagon}
          alt="hexagon"
          blurDataURL={heaxagon}
          width={960}
          height={540}
        />
      )}

      <div
        id="cta-content"
        className={clsx('mx-auto lg:max-w-[1050px]', { 'text-white': dark, 'text-black': !dark })}>
        {!!subtitle && (
          <div
            id="cta-subtitle"
            className={clsx('mb-4 font-bold uppercase text-base', {
              'text-white': dark,
              'text-black': !dark,
            })}>
            {subtitle}
          </div>
        )}

        {!!title && (
          <h2
            className={clsx('text-heading-about font-bold lg:-mt-2', {
              'text-white': dark,
              'text-black': !dark,
            })}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}

        {!!text && (
          <div className="flex flex-col items-center justify-center text-base mt-8">
            {text.split('\n').map((line, index) => (
              <p
                key={index}
                className={clsx('mt-2 max-w-[80vw] whitespace-pre-line lg:max-w-[700px]', {
                  'text-white': dark,
                  'text-black': !dark,
                })}>
                {line}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-14">
          <Button className="flex justify-center text-base" href={primaryHref} type="primary">
            {primaryCtaText}
          </Button>

          {showEstimationButton && (
            <Button
              className="flex justify-center text-base"
              href={t('common:projectEstimationSlug')}
              type={dark ? 'outline' : 'outline-dark'}>
              {t('common:estimateProject')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
